import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';

import {
  ArrowRight, ChevronDown, Star,
  LayoutDashboard, MonitorSmartphone,
  Megaphone, Headphones, Rocket, SearchCheck,
  MessageSquareText, TrendingUp, ShieldCheck, Layers,
} from 'lucide-react';
import { useState } from 'react';
import PageGlow from "@/components/PageGlow";
import LazyDemoSection from "@/components/portal/LazyDemoSection";
import TrustLogos from "@/components/TrustLogos";
import HeroWave from "@/components/HeroWave";
import GoogleRatingBadge from "@/components/GoogleRatingBadge";
import { FEATURED_TESTIMONIAL, TESTIMONIALS } from "@/data/testimonials";

const GRAD = '#5048E5';

// ── Semantic colour helpers (respond to light/dark via CSS vars) ──────────────
const fg       = 'hsl(var(--foreground))';
const fgMuted  = 'hsl(var(--muted-foreground))';
const fgSubtle = 'color-mix(in srgb, hsl(var(--muted-foreground)) 90%, hsl(var(--foreground)))';
const surface  = 'hsl(var(--card))';
const border   = 'hsl(var(--border))';
const indigo   = 'hsl(243 75% 59%)';
const coral    = 'hsl(14 100% 57%)';

// ── FAQ data & components ─────────────────────────────────────────────────────
import { FAQ_CATEGORIES } from './index.seo';
// JSON-LD lives in ./index.seo so route head() stays lightweight.




function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden transition-all" style={{ borderBottom: `1px solid ${border}` }}>
      <button onClick={() => setOpen(v => !v)} className="flex w-full items-center justify-between gap-4 py-4 text-left" aria-expanded={open}>
        <span className="pr-4 text-sm font-semibold leading-snug" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fg }}>{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
          <ChevronDown size={16} style={{ color: fgMuted }} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
            <p className="pb-5 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const SERVICES = [
  { Icon: Rocket,          color: indigo, title: 'Vision to Launch',             href: '/services/vision-to-launch',  desc: 'Brand direction, visual identity, social media branding, SEO, AEO & GEO foundations, Google Business setup, company registration support, and a full launch checklist.' },
  { Icon: LayoutDashboard, color: indigo, title: 'AI Web & App Design',          href: '/services/ai-web-app-design', desc: 'We build or improve your website or app — fast, modern, and fully yours to own.' },
  { Icon: MonitorSmartphone, color: indigo, title: 'Branded Client Portal & App', href: '/services/client-portal',    desc: 'Your own branded client portal and mobile app — clients track progress, sign agreements, pay invoices, message you and book calls, all under your brand.' },
  { Icon: Megaphone,       color: indigo, title: 'Marketing',                    href: '/services/marketing',         desc: 'PPC, Google Ads, Facebook and Instagram Ads set up and managed around your real business goals.' },
  { Icon: Headphones,      color: indigo, title: 'Ongoing Support',              href: '/services/ongoing-support',   desc: 'Website updates, SEO, AEO & GEO improvements, technical support, and general digital help — your long-term partner.' },
  { Icon: SearchCheck,     color: coral,  title: 'Software Audit & Consolidation', href: '/services/software-audit', desc: 'A fixed £495 audit of every tool you pay for — what to cancel, what to keep, and one clear consolidated setup, managed for you.' },
];

const PROCESS = [
  { n: '01', title: 'Discovery call', desc: 'A relaxed, no-pressure chat about your business, where you’re stuck, and what you want to achieve.' },
  { n: '02', title: 'Audit & strategy', desc: 'We dig into your current setup, spot what’s wasting money, and build a clear, prioritised plan.' },
  { n: '03', title: 'Your client portal', desc: 'Your own secure portal — track progress, sign agreements, pay invoices and message us in real time.' },
  { n: '04', title: 'Build & launch', desc: 'AI-assisted workflows let us move fast without cutting corners. You approve before anything goes live.' },
  { n: '05', title: 'Ongoing support', desc: 'We don’t disappear after launch — ongoing optimisation, growth, and a partner you can call.' },
];


// Testimonials live in one editable data array — see src/data/testimonials.ts
// for the featured card, the additional-reviews array, and how to add new ones.

// ── Data ──────────────────────────────────────────────────────────────────────

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } } as const;
const stagger = { visible: { transition: { staggerChildren: 0.07 } } } as const;

// ── Gradient text span ────────────────────────────────────────────────────────
function GradText({ children, grad }: { children: React.ReactNode; grad: string }) {
  return (
    <span style={{ background: grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
      {children}
    </span>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const [activeStep, setActiveStep] = useState(-1);

  return (
    <main style={{ background: 'hsl(var(--background))' }}>


      {/* ══════════════════════════════════════════════════════
          HERO — single responsive layout (no duplicate DOM)
      ══════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden px-5 sm:px-8 lg:px-10"
        style={{
          background: 'hsl(var(--background))',
          display: 'flex',
          alignItems: 'flex-start',
          paddingTop: '0.5rem',
          paddingBottom: 'clamp(3rem, 8vh, 6.5rem)',
        }}
      >
        <HeroWave fadeTop={6} fadeBottom={70} bleed="bottom" />
        <PageGlow variant="home" />

        <div className="relative z-10 mx-auto w-full max-w-7xl">
          <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2 lg:gap-10">

            {/* ── Text column ── */}
            <div>



              <motion.h1
                className="mb-3 font-bold leading-[1.04]"
                style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.75rem, 4.6vw, 3.25rem)', color: fg }}
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="block">Baffled by disconnected tools</span>
                <span className="block">and a scattered</span>
                <span className="block">online presence?</span>
              </motion.h1>

              {/* Brand underline accent */}
              <motion.div
                className="mb-4 h-0.5 rounded-full"
                style={{ width: 'clamp(7rem, 22vw, 11rem)', transformOrigin: 'left', background: 'linear-gradient(90deg, hsl(243 75% 59%) 0%, hsl(14 100% 57%) 100%)' }}
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ duration: 0.35, delay: 0.3 }}
              />

              <motion.p
                className="mb-4 max-w-lg text-sm leading-relaxed"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.25 }}
              >
                We're the one partner that runs it all — your website, client portal, marketing and every tool your business relies on — consolidated into <span style={{ color: fg, fontWeight: 500 }}>one clear setup</span>, managed for you in plain English.
              </motion.p>


              <motion.div
                className="flex flex-wrap gap-2 sm:gap-3"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.4 }}
              >
                <Link to="/get-started" className="btn-glow group inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold sm:px-7 sm:py-3.5 sm:text-base" style={{ background: 'hsl(243 75% 59%)', fontFamily: 'Plus Jakarta Sans, sans-serif', color: '#ffffff' }}>
                  Get Started
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5 shrink-0 sm:hidden" style={{ color: '#ffffff' }} />
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5 shrink-0 hidden sm:inline" style={{ color: '#ffffff' }} />
                </Link>
                <Link to="/services" className="btn-glass-hover inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold sm:px-7 sm:py-3.5 sm:text-base" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(16px) saturate(140%)', WebkitBackdropFilter: 'blur(16px) saturate(140%)', border: '1px solid var(--glass-border)', boxShadow: 'var(--glass-shadow)', color: 'var(--glass-color)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Our Services
                </Link>
              </motion.div>
              <TrustLogos />
            </div>

            {/* ── Featured client portal demo ── */}
            <motion.div
              className="h-full w-full"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.3 }}
            >
              <LazyDemoSection
                load={() => import("@/components/portal/PortalDemo")}
                componentProps={{ variant: "hero" as const }}
                className="mx-auto h-[78vh] w-full max-w-[430px] md:max-w-[1040px] lg:h-full"
                minHeight="min(760px, 82vh)"
              />

            </motion.div>


          </div>
        </div>

      </section>



      {/* ══════════════════════════════════════════════════════
          SERVICES
      ══════════════════════════════════════════════════════ */}
      <section className="-mt-20 px-6 pb-24 pt-44" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-7xl">
          <motion.div className="mb-16 text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35 }}>
            <h2 className="mb-6 font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', lineHeight: 1.1, color: fg }}>
              Everything you need.<br />Explained simply.
            </h2>
            <p className="mx-auto max-w-2xl text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              We don't try to sell you things you don't need. We look at your business, figure out where the bottlenecks are, and deploy the right digital tools to fix them.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 gap-5 md:grid-cols-2" variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {SERVICES.map(({ Icon, color, title, href, desc }) => (
              <motion.div
                key={title}
                className="flex flex-col"
                variants={fadeUp}
                transition={{ duration: 0.4 }}
              >
                <Link
                  to={href}
                  className="group flex h-full gap-5 rounded-2xl p-6 transition-all hover:-translate-y-0.5"
                  style={{ background: surface, border: `1px solid ${border}`, textDecoration: 'none' }}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-110" style={{ background: `${color}18` }}>
                    <Icon size={20} style={{ color }} />
                  </div>
                  <div>
                    <h3 className="mb-1.5 font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h3>
                    <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{desc}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.p className="mt-10 text-sm text-center" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.1 }}
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
            All our consultants are trained across every service — one person who knows your business inside out.{' '}
            <Link to="/about" className="font-semibold hover:underline" style={{ color: GRAD }}>Meet the team →</Link>
          </motion.p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          PROCESS — compact 5-step strip (client portal = step 3)
      ══════════════════════════════════════════════════════ */}

      <section className="px-6 py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl">
          <motion.div className="mb-8 text-center">
            <h2 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', color: fg }}>
              How it works.
            </h2>
          </motion.div>
          {/* Desktop: horizontal timeline */}
          <div className="relative hidden md:block" onMouseLeave={() => setActiveStep(-1)}>
            {/* base track — always visible */}
            <div className="absolute left-0 right-0" style={{ top: '2.5rem', height: '2px', background: GRAD, opacity: 0.35 }} />
            {/* laser beam — intensifies on hover */}
            <motion.div
              className="absolute left-0"
              style={{
                top: '2.4rem',
                height: '3px',
                borderRadius: '3px',
                background: 'linear-gradient(90deg, transparent 0%, #5048E5 18%, #a855f7 55%, hsl(14 100% 57%) 100%)',
                boxShadow: '0 0 10px 1px hsl(243 75% 59% / 0.7), 0 0 22px 3px hsl(243 75% 59% / 0.35)',
                pointerEvents: 'none',
                zIndex: 5,
              }}
              initial={false}
              animate={{
                width: activeStep < 0 ? '0%' : `${((activeStep + 0.5) / PROCESS.length) * 100}%`,
                opacity: activeStep < 0 ? 0 : 1,
              }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.span
                className="absolute"
                style={{
                  right: -6, top: '50%', width: 12, height: 12, marginTop: -6, borderRadius: '50%',
                  background: 'hsl(14 100% 57%)',
                  boxShadow: '0 0 12px 3px hsl(14 100% 57% / 0.9), 0 0 24px 6px hsl(243 75% 59% / 0.5)',
                }}
                animate={{ scale: activeStep < 0 ? 0 : [1, 1.35, 1] }}
                transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
              />
            </motion.div>
            <motion.div className="flex justify-between gap-4">
              {PROCESS.map(({ n, title, desc }, i) => {
                const lit = activeStep >= 0 && i <= activeStep;
                return (
                  <motion.div key={n} className="flex cursor-pointer flex-col items-center text-center" onMouseEnter={() => setActiveStep(i)} onFocus={() => setActiveStep(i)} tabIndex={0} style={{ flex: '1 1 0%', maxWidth: 220 }}>
                    <motion.div
                      className="relative z-10 mb-5 flex items-center justify-center rounded-full"
                      style={{ width: 80, height: 80, background: 'hsl(var(--background))', borderWidth: '3px', borderStyle: 'solid' }}
                      animate={{
                        borderColor: lit ? 'hsl(243 75% 59%)' : 'hsl(243 75% 59% / 0.5)',
                        boxShadow: lit
                          ? '0 0 22px 3px hsl(243 75% 59% / 0.55), 0 0 6px 2px hsl(14 100% 57% / 0.35), inset 0 0 14px 2px hsl(243 75% 59% / 0.12)'
                          : '0 0 14px 2px hsl(243 75% 59% / 0.25), inset 0 0 10px 1px hsl(243 75% 59% / 0.06)',
                        scale: i === activeStep ? 1.08 : 1,
                      }}
                      transition={{ duration: 0.35 }}
                    >
                      <span className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', backgroundImage: lit ? `linear-gradient(135deg, ${GRAD} 0%, hsl(14 100% 57%) 100%)` : `linear-gradient(135deg, ${GRAD} 0%, ${GRAD} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{n}</span>
                    </motion.div>
                    <h3 className="mb-1.5 text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h3>
                    <p className="text-xs leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{desc}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Mobile: vertical timeline */}
          <div className="relative md:hidden" onMouseLeave={() => setActiveStep(-1)}>
            <div className="absolute left-10 top-0 bottom-0 w-0.5" style={{ background: `linear-gradient(to bottom, ${GRAD} 0%, hsl(14 100% 57%) 100%)`, opacity: 0.35 }} />
            <motion.div className="flex flex-col gap-8">
              {PROCESS.map(({ n, title, desc }, i) => {
                const lit = activeStep >= 0 && i <= activeStep;
                return (
                  <motion.div key={n} className="flex cursor-pointer items-start gap-5" onMouseEnter={() => setActiveStep(i)} onFocus={() => setActiveStep(i)} tabIndex={0}>
                    <motion.div
                      className="relative z-10 flex shrink-0 items-center justify-center rounded-full"
                      style={{ width: 80, height: 80, background: 'hsl(var(--background))', borderWidth: '3px', borderStyle: 'solid' }}
                      animate={{
                        borderColor: lit ? 'hsl(243 75% 59%)' : 'hsl(243 75% 59% / 0.5)',
                        boxShadow: lit
                          ? '0 0 22px 3px hsl(243 75% 59% / 0.55), 0 0 6px 2px hsl(14 100% 57% / 0.35), inset 0 0 14px 2px hsl(243 75% 59% / 0.12)'
                          : '0 0 14px 2px hsl(243 75% 59% / 0.25), inset 0 0 10px 1px hsl(243 75% 59% / 0.06)',
                        scale: i === activeStep ? 1.06 : 1,
                      }}
                      transition={{ duration: 0.35 }}
                    >
                      <span className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', backgroundImage: lit ? `linear-gradient(135deg, ${GRAD} 0%, hsl(14 100% 57%) 100%)` : `linear-gradient(135deg, ${GRAD} 0%, ${GRAD} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{n}</span>
                    </motion.div>
                    <div className="pt-1">
                      <h3 className="mb-1 text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h3>
                      <p className="text-xs leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
          <motion.div className="mt-8 text-center">
            <Link to="/services" className="group inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-70" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              See the full process <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </div>
      </section>

{/* ══════════════════════════════════════════════════════
          WHY US
      ══════════════════════════════════════════════════════ */}
      <section className="px-6 py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl">
          <motion.div>
            <h2 className="mb-2 font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.1, color: fg }}>
              Technology shouldn't feel like a second job.
            </h2>
            <p className="mb-10 max-w-xl text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              You started your business to do what you love — not to wrestle with DNS settings or figure out why your ads are burning money. We handle all of it.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 gap-x-12 gap-y-8 sm:grid-cols-2">
            {[
              { icon: MessageSquareText, title: "Plain English, always.", body: "No jargon, no acronym soup. Every recommendation comes with a clear reason why it matters to your business." },
              { icon: TrendingUp,        title: "ROI, not vanity metrics.", body: "Follower counts don't pay bills. We track what actually moves the needle — leads, conversions, revenue." },
              { icon: ShieldCheck,       title: "You own everything.", body: "Your domain, your code, your accounts. We build it, you keep it — no lock-in, no hostage situations." },
              { icon: Layers,            title: "One partner, not four agencies.", body: "Web, SEO, ads, and automation under one roof. No finger-pointing, no briefing the same thing four times." },
            ].map(({ icon: Icon, title, body }, i) => (
              <motion.div key={title} className="flex gap-4">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: 'hsla(243,75%,59%,0.1)' }}>
                  <Icon size={16} style={{ color: GRAD }} />
                </div>
                <div>
                  <p className="mb-1 text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</p>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{body}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          TWO WAYS TO START
      ══════════════════════════════════════════════════════ */}
      <section className="px-6 py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl">
          <motion.h2 className="mb-8 text-center font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', color: fg }}>
            Two ways to start.
          </motion.h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Link
              to="/services/vision-to-launch"
              className="group flex flex-col rounded-2xl p-7 transition-transform hover:-translate-y-0.5"
              style={{ background: surface, border: `1px solid ${border}` }}
            >
              <div className="mb-2 text-xs font-semibold uppercase tracking-widest" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>For new & growing businesses</div>
              <h3 className="mb-2 text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>Starting or growing?</h3>
              <p className="mb-4 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                Begin your digital journey the right way — branding, website, tools and support, all set up and connected from day one.
              </p>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Vision to Launch <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
            <Link
              to="/services/software-audit"
              className="group flex flex-col rounded-2xl p-7 transition-transform hover:-translate-y-0.5"
              style={{ background: 'hsla(14,100%,57%,0.06)', border: `1px solid hsla(14,100%,57%,0.28)` }}
            >
              <div className="mb-2 text-xs font-semibold uppercase tracking-widest" style={{ color: coral, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>For established businesses</div>
              <h3 className="mb-2 text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>Tools everywhere already?</h3>
              <p className="mb-4 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                A £495 Software Audit & Consolidation — find out what you're really paying for, and get it all connected and managed as one.
              </p>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Software Audit & Consolidation <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>








      {/* ══════════════════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════════════════ */}
      <section className="px-6 py-24" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-7xl">
          <motion.h2 className="mb-14 text-center font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 4vw, 3rem)', color: fg }}>
            Don't just take our word for it.
          </motion.h2>

          {/* ── Featured testimonial: photo-forward card linked to the case study ── */}
          {(() => {
            const t = FEATURED_TESTIMONIAL;
            const inner = (
              <div
                className="grid gap-8 rounded-3xl p-8 transition-all md:grid-cols-[auto_1fr] md:p-10"
                style={{ background: surface, border: `1px solid ${border}`, boxShadow: '0 20px 60px hsl(243 75% 59% / 0.12)' }}
              >
                <div className="flex flex-col items-center gap-3 text-center md:w-56">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={`${t.name} — ${t.role}, ${t.company}`}
                      width={160}
                      height={160}
                      loading="lazy"
                      className="h-32 w-32 rounded-2xl object-cover md:h-40 md:w-40"
                      style={{ border: '3px solid hsl(243 75% 59% / 0.5)', objectPosition: '38% 30%' }}
                    />
                  ) : (
                    <div className="flex h-32 w-32 items-center justify-center rounded-2xl text-4xl font-bold md:h-40 md:w-40" style={{ background: 'hsla(243,75%,59%,0.1)', color: GRAD, fontFamily: 'Outfit, sans-serif' }}>
                      {t.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{t.name}</div>
                    <div className="text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgSubtle }}>{t.role}, {t.company}</div>
                  </div>
                  <div className="flex gap-1" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} size={15} fill="hsl(45 100% 50%)" style={{ color: 'hsl(45 100% 50%)' }} />
                    ))}
                  </div>
                </div>

                <div className="relative flex flex-col justify-center">
                  <span className="absolute -top-6 right-0 select-none font-bold md:-top-4" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '6rem', lineHeight: 1, color: 'hsl(243 75% 59% / 0.08)' }} aria-hidden="true">"</span>
                  <p className="text-lg leading-relaxed md:text-xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fg }}>"{t.quote}"</p>
                  {t.caseStudyUrl && (
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: 'hsl(243 75% 65%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      Read the {t.company} case study <ArrowRight size={15} />
                    </span>
                  )}
                </div>
              </div>
            );

            return t.caseStudyUrl ? (
              <motion.div>
                <Link to={t.caseStudyUrl} className="group block hover:-translate-y-0.5" style={{ textDecoration: 'none' }} aria-label={`Read the ${t.company} case study`}>
                  {inner}
                </Link>
              </motion.div>
            ) : (
              <motion.div>
                {inner}
              </motion.div>
            );
          })()}

          {/* Google rating badge — hidden until enabled in src/config/brand.ts */}
          <div className="mt-8 flex justify-center empty:hidden">
            <GoogleRatingBadge />
          </div>

          {/* ── Additional testimonials + placeholder slots ──
              Real reviews come from the TESTIMONIALS array in
              src/data/testimonials.ts. Empty "Your review here" slots fill the
              remaining space so the section always looks intentional. */}
          <motion.div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {TESTIMONIALS.map(({ name, role, company, quote, photo, disclosure }) => (
              <motion.div key={`${name}-${company}`} className="relative flex flex-col rounded-2xl p-7" style={{ background: surface, border: `1px solid ${border}` }}>
                <span className="absolute right-5 top-3 select-none font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '4.5rem', lineHeight: 1, color: 'hsl(243 75% 59% / 0.08)' }} aria-hidden="true">"</span>
                <div className="mb-4 flex gap-1" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} size={13} fill="hsl(45 100% 50%)" style={{ color: 'hsl(45 100% 50%)' }} />
                  ))}
                </div>
                <p className="mb-6 flex-1 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>"{quote}"</p>
                <div className="flex items-center gap-3">
                  {photo ? (
                    <img src={photo} alt={`${name} — ${role}, ${company}`} width={44} height={44} loading="lazy" className="h-11 w-11 shrink-0 rounded-full object-cover" style={{ border: `2px solid hsl(243 75% 59% / 0.4)` }} />
                  ) : (
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-bold text-sm" style={{ background: 'hsla(243,75%,59%,0.1)', color: '#5048E5', fontFamily: 'Outfit, sans-serif' }}>
                      {name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{name}</div>
                    <div className="mt-0.5 text-xs" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgSubtle }}>{role}, {company}</div>
                  </div>
                </div>
                {disclosure && (
                  <p className="mt-4 text-xs leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgSubtle }}>
                    {disclosure}
                  </p>
                )}
              </motion.div>
            ))}


          </motion.div>

          <motion.div className="mt-10 text-center">
            <a
              href="https://g.page/r/CQFcYuHGKROrEAE/review"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-glass-hover inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all"
              style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)', color: 'var(--glass-color)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {/* Google "G" logo */}
              <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Leave us a review on Google
            </a>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FAQ TEASER
      ══════════════════════════════════════════════════════ */}
      <section className="px-6 py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-3xl">
          <motion.div className="mb-6 text-center">
            <h2 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', color: fg }}>
              Common questions
            </h2>
          </motion.div>
          <motion.div className="flex flex-col gap-3">
            {FAQ_CATEGORIES[0].faqs.slice(0, 3).map(({ q, a }) => (
              <motion.div key={q}>
                <FaqItem q={q} a={a} />
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="mt-6 text-center">
            <Link to="/faq" className="group inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-70" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              See all FAQs <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════════════ */}
      <section className="relative -mt-10 px-6 pb-20 pt-30" style={{ background: `linear-gradient(180deg, hsl(var(--background)) 0%, hsla(243,75%,59%,0.06) 45%, hsla(14,100%,57%,0.04) 100%)` }}>
        <HeroWave bleed="both" />
        <div className="mx-auto max-w-2xl text-center relative">
          <motion.div>
            <h2 className="mb-3 font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 4vw, 3rem)', color: fg, lineHeight: 1.1 }}>
              Ready to stop guessing?
            </h2>
            <p className="mb-8 text-base leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted, maxWidth: '40ch', margin: '0 auto 2rem' }}>
              Book a free 30-minute discovery call. No jargon, no hard sell — just an honest look at what your business actually needs.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/get-started"
                className="btn-glow group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-bold transition-all"
                style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Get Started
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>

            </div>
            <p className="mt-6 text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Already a client?{' '}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: GRAD }}>Go to your portal →</Link>
            </p>
          </motion.div>
        </div>
      </section>

    </main>
  );
}
